import { ContractStateDto, type ContractReportQueryDto } from "../../autogen/bff-api";
import { urls } from "../../urls";
import { base64Encode } from "../utils/base64";

type Params = Readonly<Record<string, string | undefined>>;

export interface LegacyPageRedirect {
  path: string;
  to: (params: Params, search: URLSearchParams) => string;
  keepQuery?: boolean;
}

const commaList = (search: URLSearchParams, key: string): string[] | undefined => {
  const ids = (search.get(key) ?? "").split(",").filter(Boolean);
  return ids.length > 0 ? ids : undefined;
};

export const contractListQuery = (state: ContractStateDto, search: URLSearchParams): string => {
  const query: Partial<ContractReportQueryDto> = {
    state,
    titleFilter: search.get("title") || undefined,
    projectIds: commaList(search, "project"),
    counterpartyIds: commaList(search, "counterparty"),
    internalPartyIds: commaList(search, "internalParty"),
    internalOwnerIds: commaList(search, "owner"),
    templateIds: commaList(search, "type"),
    statuses: commaList(search, "status") as ContractReportQueryDto["statuses"],
  };
  const defined = Object.fromEntries(Object.entries(query).filter(([, value]) => value !== undefined));
  return `${urls.app.contracts.viewAll}?q=${encodeURIComponent(base64Encode(JSON.stringify(defined)))}`;
};

export const legacyPageRedirects: LegacyPageRedirect[] = [
  { path: urls.contracts.viewAll, to: () => urls.app.contracts.viewAll, keepQuery: true },
  { path: urls.contracts.viewDrafts, to: (_, search) => contractListQuery(ContractStateDto.Draft, search) },
  { path: urls.contracts.viewPublished, to: (_, search) => contractListQuery(ContractStateDto.Published, search) },
  { path: urls.contracts.search, to: () => urls.app.contracts.search },
  { path: urls.contracts.searchV2, to: () => urls.app.contracts.search, keepQuery: true },
  { path: urls.contracts.viewTasks, to: () => urls.app.contracts.tasks },
  { path: urls.contracts.settings, to: () => `${urls.app.settings}?tab=contract-types` },
  {
    path: urls.contracts.edit.paths.details.pathname,
    to: ({ contractId }) => urls.app.contracts.edit.go(contractId ?? ""),
    keepQuery: true,
  },
  {
    path: urls.contracts.viewSingle.pathname,
    to: ({ contractId }) => urls.app.contracts.view.go(contractId ?? ""),
    keepQuery: true,
  },
  { path: urls.projects.viewAll, to: () => urls.app.projects.viewAll, keepQuery: true },
  { path: urls.projects.view.pathname, to: ({ projectId }) => urls.app.projects.view.go(projectId ?? "") },
  { path: urls.notifications, to: () => urls.app.notifications },
  { path: urls.organizations.search, to: () => urls.app.organizations, keepQuery: true },
  {
    path: `${urls.organizations.profile.pathname}/*`,
    to: (params) => [urls.app.organizationProfile.pathname, params["*"]].filter(Boolean).join("/"),
    keepQuery: true,
  },
  { path: urls.myProfile.organization.edit, to: () => urls.app.organizationProfileEdit },
  { path: urls.myProfile.organization.manageUsers, to: () => urls.app.users },
  { path: urls.myProfile.organization.settings, to: () => urls.app.settings, keepQuery: true },
  { path: urls.myProfile.personal.view, to: () => urls.app.profile },
  { path: urls.myProfile.personal.edit, to: () => urls.app.profileEdit },
];
